import React, { useState } from "react";
import { BsLayoutSidebar, BsSun, BsMoon } from "react-icons/bs";
import { useTheme } from "../../contexts/ThemeContext";

const Header = ({
  email,
  signOutRedirect,
  showUpload,
  toggleSidebar,
  sidebarVisible,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  return (
    <nav className="navbar navbar-expand-lg navbar-light bg-light shadow-sm">
      <div className="container-fluid px-3">
        {/* Mobile/Tablet Layout */}
        <div className="d-lg-none w-100">
          <div
            className="d-flex justify-content-between align-items-center py-2"
            style={{ minHeight: "60px" }}
          >
            {/* Hamburger + Logo - with flex properties to prevent squeezing */}
            <div
              className="navbar-brand d-flex align-items-center flex-shrink-0"
              style={{ minWidth: "0" }}
            >
              <button
                className={`btn btn-sm me-2 btn-animate flex-shrink-0 ${
                  sidebarVisible ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
                title={sidebarVisible ? "Hide sidebar" : "Show sidebar"}
              >
                <BsLayoutSidebar size={20} />
              </button>
              <img
                src="MPEG-G.png"
                alt="MPEG-G Logo"
                className="img-fluid flex-shrink-0"
                style={{ maxHeight: "45px", width: "auto", minWidth: "40px" }}
              />
            </div>

            {/* Dark Mode Toggle and User dropdown on mobile - with proper spacing */}
            <div
              className="d-flex align-items-center flex-shrink-0"
              style={{ gap: "0.5rem" }}
            >
              <button
                className={`btn btn-sm rounded-pill btn-animate pulse-hover flex-shrink-0 ${
                  isDarkMode ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
                style={{ minWidth: "40px" }}
              >
                {isDarkMode ? <BsSun size={18} /> : <BsMoon size={18} />}
              </button>

              {email && (
                <div className="dropdown flex-shrink-0">
                  <button
                    className="btn btn-primary dropdown-toggle btn-sm btn-animate"
                    type="button"
                    id="userDropdownMobile"
                    onClick={toggleDropdown}
                    aria-expanded={isDropdownOpen}
                    style={{
                      minWidth: "80px",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {email.length > 15 ? `${email.substring(0, 12)}...` : email}
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end smooth-transition ${
                      isDropdownOpen ? "show" : ""
                    }`}
                    aria-labelledby="userDropdownMobile"
                  >
                    <li>
                      <button
                        className="dropdown-item smooth-hover"
                        onClick={showUpload}
                      >
                        Select File
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item smooth-hover"
                        onClick={signOutRedirect}
                      >
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="d-none d-lg-flex w-100">
          <div className="d-flex align-items-center w-100 py-1 px-2">
            {/* Hamburger + Logo on the left */}
            <div
              className="d-flex align-items-center flex-shrink-0"
              style={{ minWidth: "200px" }}
            >
              <button
                className={`btn btn-sm me-3 btn-animate flex-shrink-0 ${
                  sidebarVisible ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
                title={sidebarVisible ? "Hide sidebar" : "Show sidebar"}
                style={{ minWidth: "44px" }}
              >
                <BsLayoutSidebar size={24} />
              </button>
              <div className="navbar-brand ms-1 flex-shrink-0">
                <img
                  src="MPEG-G.png"
                  alt="MPEG-G Logo"
                  height="70"
                  style={{ width: "auto", minWidth: "60px" }}
                />
              </div>
            </div>

            {/* Center Text - takes remaining space */}
            <div className="flex-grow-1 d-flex justify-content-center align-items-center mx-3">
              <h2
                className="header-text mb-0 fw-bold text-center"
                style={{
                  textShadow: "0 0 8px rgba(76, 166, 168, 0.4)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                MPEG-G Variant Explorer
              </h2>
            </div>

            {/* User Info with Dropdown */}
            <div
              className="d-flex justify-content-end align-items-center flex-shrink-0"
              style={{ gap: "0.5rem", minWidth: "200px" }}
            >
              {/* Dark Mode Toggle */}
              <button
                className={`btn rounded-pill btn-animate pulse-hover flex-shrink-0 ${
                  isDarkMode ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={toggleTheme}
                aria-label="Toggle dark mode"
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
                style={{ minWidth: "44px" }}
              >
                {isDarkMode ? <BsSun size={20} /> : <BsMoon size={20} />}
              </button>

              {email && (
                <div className="dropdown flex-shrink-0">
                  <button
                    className="btn btn-primary dropdown-toggle btn-animate"
                    type="button"
                    id="userDropdown"
                    onClick={toggleDropdown}
                    aria-expanded={isDropdownOpen}
                    style={{
                      minWidth: "100px",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {email}
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end smooth-transition ${
                      isDropdownOpen ? "show" : ""
                    }`}
                    aria-labelledby="userDropdown"
                  >
                    <li>
                      <button
                        className="dropdown-item smooth-hover"
                        onClick={showUpload}
                      >
                        Select File
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item smooth-hover"
                        onClick={signOutRedirect}
                      >
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
